import { createReducer, createSelector, on } from '@ngrx/store';
import { HealthyWorkplaceSurveyManagementDTO } from '@iota/inh-core-v1-management-client';
import {
    AddSurveyUrl,
    AddSurveyUrlFailure,
    AddSurveyUrlSuccess,
    ApproveSurveyFailure,
    ApproveSurveySuccess,
    CompleteSurveySuccess,
    GenerateProjectSetup,
    GenerateProjectSetupFailure,
    GenerateProjectSetupSuccess,
    LoadSurvey,
    LoadSurveyDescriptionFailure,
    LoadSurveyDescriptionSuccess,
    LoadSurveyFailure,
    LoadSurveys,
    LoadSurveysFailure,
    LoadSurveysSuccess,
    LoadSurveySuccess,
    RejectSurveyFailure,
    RejectSurveySuccess,
    UpdateSurvey
} from '../actions/hwp-survey.actions';
import { AppState } from '../store';
import { companies } from './company.reducer';
import { allOffices } from './office.reducer';

export interface HwpState {
    surveys: HealthyWorkplaceSurveyManagementDTO[];
    loading: boolean;
    error: any;
    currentSurvey: HealthyWorkplaceSurveyManagementDTO | null;
    currentSurveyLoading: boolean;
    urlLoading: boolean;
    urlLoadingError: any;
    currentSurveySetup: string[];
    currentSurveySetupLoading: boolean;
    currentSurveySetupError: boolean;
    configurationDescription: Blob;
    configurationDescriptionError: boolean;
}

export const initialState: HwpState = {
    surveys: [],
    loading: false,
    error: null,
    currentSurvey: null,
    currentSurveyLoading: true,
    urlLoading: false,
    urlLoadingError: null,
    currentSurveySetup: [],
    currentSurveySetupLoading: false,
    currentSurveySetupError: false,
    configurationDescription: null,
    configurationDescriptionError: false
};

export const hwpSurveyReducer = createReducer(
    initialState,
    on(LoadSurveys, (state) => ({
        ...state,
        loading: true,
        error: null
    })),
    on(LoadSurveysSuccess, (state, { surveys }) => ({
        ...state,
        surveys,
        loading: false,
        currentSurvey: null
    })),
    on(LoadSurveysFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error
    })),
    on(LoadSurvey, (state) => ({
        ...state,
        currentSurveyLoading: true,
        error: null
    })),
    on(LoadSurveySuccess, (state, { survey }) => ({
        ...state,
        currentSurvey: survey,
        currentSurveyLoading: false
    })),
    on(LoadSurveyFailure, (state, { error }) => ({
        ...state,
        currentSurveyLoading: false,
        error
    })),
    on(UpdateSurvey, (state, { surveyId, newState }) => {
        const updatedSurveys = state.surveys.map((survey) =>
            survey.id === surveyId
                ? {
                      ...survey,
                      state: newState
                  }
                : survey
        );

        return {
            ...state,
            surveys: updatedSurveys
        };
    }),
    on(AddSurveyUrl, (state, { surveyId, newUrl }) => {
        const updatedSurveys = state.surveys.map((survey) =>
            survey.id === surveyId
                ? {
                      ...survey,
                      surveyUrl: newUrl
                  }
                : survey
        );

        return {
            ...state,
            urlLoading: true,
            urlLoadingError: null,
            surveys: updatedSurveys
        };
    }),

    on(ApproveSurveySuccess, (state, { survey }) => ({
        ...state,
        currentSurvey: survey
    })),
    on(ApproveSurveyFailure, (state, { error }) => ({
        ...state,
        error
    })),
    on(RejectSurveySuccess, (state, { survey }) => ({
        ...state,
        currentSurvey: survey
    })),
    on(RejectSurveyFailure, (state, { error }) => ({
        ...state,
        error
    })),
    on(AddSurveyUrlSuccess, (state, { survey }) => ({
        ...state,
        urlLoading: false,
        urlLoadingError: null,
        currentSurvey: survey
    })),
    on(AddSurveyUrlFailure, (state, { error }) => ({
        ...state,
        urlLoading: false,
        urlLoadingError: error,
        error
    })),
    on(CompleteSurveySuccess, (state, { survey }) => ({
        ...state,
        currentSurvey: survey
    })),
    on(GenerateProjectSetup, (state) => ({
        ...state,
        currentSurveySetupLoading: true,
        currentSurveySetupError: null
    })),
    on(GenerateProjectSetupSuccess, (state, { projectSetup }) => {
        return {
            ...state,
            currentSurveySetup: projectSetup,
            currentSurvey: state.currentSurvey
                ? {
                      ...state.currentSurvey,
                      status:
                          state.currentSurvey.status === HealthyWorkplaceSurveyManagementDTO.StatusEnum.REQUESTED
                              ? HealthyWorkplaceSurveyManagementDTO.StatusEnum.PREPARING
                              : state.currentSurvey.status
                  }
                : null,
            currentSurveySetupLoading: false,
            currentSurveySetupError: false
        };
    }),
    on(GenerateProjectSetupFailure, (state) => ({
        ...state,
        currentSurveySetupLoading: false,
        currentSurveySetupError: true
    })),
    on(LoadSurveyDescriptionSuccess, (state, { surveyDescription }) => ({
        ...state,
        configurationDescription: surveyDescription,
        configurationDescriptionError: false
    })),
    on(LoadSurveyDescriptionFailure, (state) => ({
        ...state,
        configurationDescriptionError: true
    }))
);

export const hwpState = (state: AppState) => state.hwpState;

export const selectAllSurveys = createSelector(hwpState, (state: HwpState) => state.surveys);
export const selectSurveysLoading = createSelector(hwpState, (state: HwpState) => state.loading);

export const selectCurrentSurveyLoading = createSelector(hwpState, (state: HwpState) => state.currentSurveyLoading);
export const selectCurrentSurvey = createSelector(hwpState, (state: HwpState) => state.currentSurvey);
export const selectCurrentSurveySetup = createSelector(hwpState, (state: HwpState) => state.currentSurveySetup);

export const selectCurrentSurveyCompany = (companyId: string) =>
    createSelector(companies, (allCompanies) => allCompanies.find((company) => company.companyId === companyId));

export const selectCurrentSurveyOffices = (companyId: string) =>
    createSelector(selectCurrentSurveyCompany(companyId), allOffices, (selectedCompany, offices) => {
        if (!selectedCompany) return [];
        return offices.filter((office) => office.companyId === selectedCompany.companyId);
    });

export const urlLoading = createSelector(hwpState, (state: HwpState) => state.urlLoading);
export const urlLoadingError = createSelector(hwpState, (state: HwpState) => state.urlLoadingError);
export const currentSurveySetupLoading = createSelector(hwpState, (state: HwpState) => state.currentSurveySetupLoading);
export const configurationDescription = createSelector(hwpState, (state: HwpState) => state.configurationDescription);
export const currentSurveySetupError = createSelector(hwpState, (state: HwpState) => state.currentSurveySetupError);
export const configurationDescriptionError = createSelector(hwpState, (state: HwpState) => state.configurationDescriptionError);
export const downloadPreparationError = createSelector(
    currentSurveySetupError,
    configurationDescriptionError,
    (setupError, descriptionError) => setupError || descriptionError
);
